(ns minorodata.group.api
  (:require
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.app.utils :as utils]
   [minorodata.group.model :refer [->group ->groups-panel]]
   [minorodata.sandbox.model :refer [->pipeline-group]]
   [minorodata.transform.api :refer [schedule-change-capture]]
   [mold-http.core :refer [reg-event-http]]
   [re-posh.core :as re-posh]))


;; =============================================================================
;; Load Groups
;; =============================================================================

(defn fetch-query-params [params panel]
  (let [{:groups-panel/keys [page order-by order-dir]} panel
        sort (cond-> nil
               (some? order-by) (assoc :order-by order-by)
               (some? order-dir) (assoc :order-dir order-dir)
               (some? (:order-by params)) (assoc :order-by (:order-by params))
               (some? (:order-dir params)) (assoc :order-dir (:order-dir params)))]
    (utils/assoc-some {:pagination {:page           (or (:page params) page 1)
                                    :items-per-page (or (:items-per-page params) 50)}}
                      :sort sort)))


(defn fetch-groups-request [_ [{:keys [page replace?]
                                :or   {replace? false}
                                :as   params}]]
  (let [panel @(re-posh/subscribe [:groups-panel/pull])]
    (when (or (nil? page)
              (= 1 page)
              (<= page (:groups-panel/total-pages panel)))
      {:http-xhrio
       {:id        :load-groups-request
        :url       "/api/transformations"
        :params    (fetch-query-params params panel)
        :cb-params replace?}})))

(defn fetch-groups-callback [_ [error resp replace?]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to load groups"]
                  [:app.api/log :error "Failed to load groups" error]]}

    (let [{:keys [results pagination sort]} resp
          {:groups-panel/keys [latest-order-index]} @(re-posh/subscribe [:groups-panel/pull])
          results-count (count results)
          [start-idx end-idx] (if replace?
                                [0 results-count]
                                [latest-order-index (+ latest-order-index results-count)])
          groups        (mapv (fn [idx group] (assoc group :order idx))
                              (range start-idx end-idx)
                              results)
          panel         (-> {:groups             groups
                             :latest-order-index end-idx}
                            (merge pagination sort)
                            ->groups-panel)]

      {:dispatch [:groups-panel/update panel {:replace replace?}]})))

(reg-event-http
 :group.api/fetch-groups
 fetch-groups-request
 fetch-groups-callback)


;; =============================================================================
;; Load a single Group
;; =============================================================================

(defn refetch-group-request [_ [{:keys [group-id]}]]
  {:http-xhrio
   {:url (str "/api/transformations/" group-id)}})

(defn refetch-group-callback [_ [error result]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to load a transformations group"]
                  [:app.api/log :error "Failed to load a transformations group" error]]}

    (let [{:groups-panel/keys [latest-order-index]} @(re-posh/subscribe [:groups-panel/pull])
          group      result
          group-idx  (or latest-order-index 0)
          full-group (-> group
                         (assoc :order group-idx)
                         ->group)]
      {:dispatch-n [[:group/add full-group]
                    [:groups-panel/update {:groups-panel/groups             full-group
                                           :groups-panel/latest-order-index (+ group-idx 1)}]]})))

(reg-event-http
 :group.api/refetch-group
 refetch-group-request
 refetch-group-callback)


;; =============================================================================
;; Update Group
;; =============================================================================

(defn update-group-request [_ [group {:keys [on-success]}]]
  (let [{:keys [id name description]} group
        params (utils/assoc-some {}
                                 :name name
                                 :description description)]
    {:http-xhrio
     {:url       (str "/api/transformations/" id)
      :method    :put
      :params    params
      :cb-params {:group-id id
                  :on-success on-success}}}))

(defn update-group-callback [_ [error resp {:keys [group-id on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to update the group"]
                  [:app.api/log :error "Failed to update the group" error]]}

    (let [group resp]
      (when on-success (on-success))
      {:dispatch-n [[:group/update [:group/id group-id] (->group group)]
                    [:unsaved-group/delete [:unsaved-group/id group-id]]
                    [:app.events/add-snack :success "Group updated"]]})))

(reg-event-http
 :group.api/update-group
 update-group-request
 update-group-callback)


;; =============================================================================
;; Create New Group
;; =============================================================================

(defn create-group-request [_ [{:keys [group-name group-description]} cb-params]]
  {:http-xhrio
   {:url    "/api/transformations"
    :method :post
    :params (utils/clean-up-query
             {:name        group-name
              :description (utils/trim? group-description)})
    :cb-params cb-params}})

(defn create-group-callback [_ [error resp {:keys [on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to create a new group"]
                  [:app.api/log :error "Failed to create a new group" error]]}

    (let [{:groups-panel/keys [latest-order-index]} @(re-posh/subscribe [:groups-panel/pull])
          {:keys [id] :as group} resp
          group-idx  (or latest-order-index 0)
          full-group (-> group
                         (assoc :order group-idx)
                         ->group)]
      (when on-success (on-success id))
      {:dispatch-n [[:group/add full-group]
                    [:pipeline-group/add (->pipeline-group group)]
                    [:groups-panel/update {:groups-panel/groups             {:group/id id}
                                           :groups-panel/latest-order-index (+ group-idx 1)}]
                    [:app.events/add-snack :success "Group created"]]
       :posthog/capture {:event (:transform/group-created posthog-events/events)}})))

(reg-event-http
 :group.api/create-group
 create-group-request
 create-group-callback)


;; =============================================================================
;; Fetch Group Schedule
;; =============================================================================

(defn fetch-schedule-request [_ [{:keys [group-id]}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" group-id "/schedule")
    :cb-params group-id}})

(defn fetch-schedule-callback [_ [error resp group-id]]
  (if (some? error)
    {:dispatch [:app.api/log :error (str "Failed to fetch schedule for group " group-id) error]}

    (let [{:keys [schedule schedulable? schedule-tip]} resp
          group {:group/schedule-spec (:schedule-spec schedule)
                 :group/schedulable? schedulable?
                 :group/schedule-tip schedule-tip}]
      {:dispatch [:group/update [:group/id group-id] group {:replace true}]})))

(reg-event-http
 :group.api/fetch-schedule
 fetch-schedule-request
 fetch-schedule-callback)


;; =============================================================================
;; Bulk Schedule Group
;; =============================================================================

(defn bulk-schedule-group-request [_ [{:keys [id schedule-spec]} {:keys [on-success]}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" id "/bulk-schedule")
    :method    :put
    :params    {:schedule-spec schedule-spec}
    :cb-params {:group-id id
                :schedule-spec schedule-spec
                :on-success on-success}}})

(defn bulk-schedule-group-callback [_ [error _ {:keys [group-id schedule-spec on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error (get-in error [:response :error])]
                  [:app.api/log :error (str "Failed to schedule group " group-id) error]
                  [:group.api/refetch-group {:group-id group-id}]
                  [:group.api/fetch-schedule {:group-id group-id}]
                  [:transform.api/fetch-transforms {:group-id group-id
                                                    :replace? true}]]}

    (let [group {:group/schedule-spec  schedule-spec
                 :group/schedulable?   true
                 :group/bulk-scheduled true}
          {:group/keys [bulk-scheduled] :as previous} @(re-posh/subscribe [:group/pull [:group/id group-id]])
          capture (schedule-change-capture "group"
                                           (when bulk-scheduled (:group/schedule-spec previous))
                                           schedule-spec)]
      (when (fn? on-success) (on-success))
      {:dispatch-n [[:group/update [:group/id group-id] group {:replace true}]
                    [:transform.api/fetch-transforms {:group-id group-id
                                                      :replace? true}]
                    [:app.events/add-snack :success "All transforms within the group were scheduled"]]
       :fx         [(when capture [:posthog/capture capture])]})))

(reg-event-http
 :group.api/bulk-schedule-group
 bulk-schedule-group-request
 bulk-schedule-group-callback)


;; =============================================================================
;; Bulk Unschedule Group
;; =============================================================================

(defn bulk-unschedule-group-request [_ [{:keys [group-id]}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" group-id "/bulk-unschedule")
    :method    :put
    :cb-params group-id}})

(defn bulk-unschedule-group-callback [_ [error _ group-id]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Unable to unschedule group"]
                  [:app.api/log :error (str "Failed to unschedule group " group-id) error]
                  [:group.api/refetch-group {:group-id group-id}]
                  [:group.api/fetch-schedule {:group-id group-id}]
                  [:transform.api/fetch-transforms {:group-id group-id
                                                    :replace? true}]]}

    (let [group {:group/schedule-spec  {:cron utils/empty-cron}
                 :group/bulk-scheduled false}]
      {:dispatch-n [[:group/update [:group/id group-id] group {:replace true}]
                    [:transform.api/fetch-transforms {:group-id group-id
                                                      :replace? true}]
                    [:app.events/add-snack :success "All transforms within the group were unscheduled"]]
       :posthog/capture {:event (:transform/schedule-updated posthog-events/events)
                         :props {:scope "group" :action "unscheduled"}}})))

(reg-event-http
 :group.api/bulk-unschedule-group
 bulk-unschedule-group-request
 bulk-unschedule-group-callback)


;; =============================================================================
;; Delete Group
;; =============================================================================

(defn delete-group-request [_ [{:keys [group-id new-page? on-success]}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" group-id)
    :method    :delete
    :cb-params {:group-id   group-id
                :on-success on-success
                :new-page?  new-page?}}})

(defn delete-group-callback [_ [error _ {:keys [group-id on-success new-page?]}]]
  (let [panel             (re-posh/subscribe [:groups-panel/pull '[{:groups-panel/active-group [:group/id]}]])
        selected-group-id (get-in @panel [:groups-panel/active-group :group/id])]
    (if (some? error)
      {:dispatch-n [[:app.events/add-snack :error "Unable to delete the group"]
                    [:app.api/log :error (str "Failed to delete the group " group-id) error]]}

      ;; Reset to /transformations url only when currently selected group has
      ;; been deleted, as only in this case there will be nothing to display.
      ;; If user deletes a group that is other than currently selected, do not
      ;; update the route.
      (do
        (when on-success (on-success))
        {:dispatch-n [(when (= selected-group-id group-id)
                        [:app.events/navigate :transformations])
                      [:group/delete [:group/id group-id]]
                      [:app.events/add-snack :success "Group deleted"]]}))))

(reg-event-http
 :group.api/delete-group
 delete-group-request
 delete-group-callback)
